import { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { CommonDialog, CommonDialogHeader } from '..';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { FastMovingSkuService } from '../../../api/services/fastMovingSkuService';
import { IUploadFastMovingSkuResponse } from '../../../types/api/response/fastmovingskuresponse';

interface IUploadFastMovingSkuDialogProps {
  onUploaded: () => void;
}

export const UploadFastMovingSkuDialog = ({ onUploaded }: IUploadFastMovingSkuDialogProps) => {
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<IUploadFastMovingSkuResponse | null>(null);
  const dispatch = useDispatch();

  const handleClose = () => {
    setIsOpenDialog(false);
    setSelectedFile(null);
    setUploadResult(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] || null);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const result = await FastMovingSkuService.uploadCsv(selectedFile);
      setUploadResult(result);
      dispatch(
        showNotification({
          message: `Uploaded version ${result.list_version}: ${result.matched} matched, ${result.unmatched} unmatched.`,
        }),
      );
      onUploaded();
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to upload fast-moving SKU list. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        sx={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
        onClick={() => setIsOpenDialog(true)}
      >
        Upload CSV
      </Button>
      {isOpenDialog ? (
        <CommonDialog
          isOpen={isOpenDialog}
          header={<CommonDialogHeader title="Upload Fast-Moving SKU List" onClose={handleClose} />}
          body={
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="text.secondary">
                Required columns: rank, sku, productName. Any other columns are ignored. The
                uploaded list does not go live until you activate it from the table.
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="outlined" component="label" size="small" disabled={uploading}>
                  Choose CSV
                  <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                </Button>
                {selectedFile && <Typography variant="body2">{selectedFile.name}</Typography>}
              </Stack>

              {uploadResult && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Version {uploadResult.list_version} uploaded
                  </Typography>
                  <Typography variant="body2">Rows: {uploadResult.row_count}</Typography>
                  <Typography variant="body2">Matched: {uploadResult.matched}</Typography>
                  <Typography variant="body2" mb={1}>
                    Unmatched: {uploadResult.unmatched}
                  </Typography>
                  {uploadResult.unmatched_skus.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {uploadResult.unmatched_skus.map((sku) => (
                        <Chip key={sku} size="small" label={sku} variant="outlined" />
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              <Button
                variant="contained"
                disabled={!selectedFile || uploading || !!uploadResult}
                onClick={handleUpload}
                sx={{ alignSelf: 'flex-start' }}
              >
                {uploading ? <CircularProgress size={18} /> : 'Upload'}
              </Button>
            </Box>
          }
          footer={undefined}
        />
      ) : (
        <></>
      )}
    </>
  );
};
